/**
 * CryptoBox panel yardımcıları.
 *
 * Ayarlar `settings` kaydında değil `provider_configs` satırındadır (bkz.
 * backend/services/cryptobox.py); bölüm kendi state'ini ve kendi Kaydet
 * düğmesini taşır. Karşılaştırma buradadır ve yalnızca **değişen** alanlar
 * gönderilir: tam gövde yazmak kayıtlı API anahtarını maskeyle ezerdi.
 */

export const CRYPTOBOX_DEFAULTS = {
  enabled: false,
  api_url: 'https://api-prod.alfa-instap-cpt.uk',
  api_key: '',
  hmac_secret: '',
  usd_try_rate: 0,
  // Kur elle takip edilmez: dış kaynaktan 15 dakikada bir okunur. Elle mod
  // yalnızca bir yedektir (kaynaklar kapalı ya da yönetici bilinçli bir kur
  // uyguluyor).
  rate_mode: 'auto',
  rate_source: 'binance',
  deposit_methods: 'checkout,USDT_TRON,BTC,ETH,USDT_ERC20,USDC_ERC20,TRON,LTC,DOGE,XRP',
  withdraw_methods: 'USDT_TRON,BTC,ETH,USDT_ERC20,USDC_ERC20,TRON,LTC,DOGE',
  deposit_enabled: true,
  withdraw_enabled: true,
  deposit_min_amount: 100,
  deposit_max_amount: 100000,
  withdraw_min_amount: 100,
  withdraw_max_amount: 100000,
  callback_registered: false,
  redirect_url: '',
  session_language: 'en',
};

// Hosted ödeme sayfası akışının yöntem kimliği; panel onu diğer zincirlerden
// ayrı çizer (iki akış aynı listede sıradan bir kutu olarak duruyordu).
export const CHECKOUT_SLUG = 'checkout';

export const CALLBACK_PATH = '/api/cryptobox/callback';
export const REDIRECT_PATH = '/hesap/para-yatir';

/**
 * Katalog — backend'deki `cryptobox.CHAINS` ile **aynı kalmalıdır**.
 * `withdraw: false` olan XRP'dir: çekim formunda hedef etiketi alanı yoktur
 * ve etiketsiz XRP gönderimi geri alınamaz.
 */
export const CRYPTOBOX_METHODS = [
  { slug: 'checkout', label: 'Ödeme sayfası (tüm zincirler)', deposit: true, withdraw: false },
  { slug: 'USDT_TRON', label: 'Tether USDT (TRC20)', deposit: true, withdraw: true },
  { slug: 'BTC', label: 'Bitcoin', deposit: true, withdraw: true },
  { slug: 'ETH', label: 'Ethereum', deposit: true, withdraw: true },
  { slug: 'USDT_ERC20', label: 'Tether USDT (ERC20)', deposit: true, withdraw: true },
  { slug: 'USDC_ERC20', label: 'USD Coin (ERC20)', deposit: true, withdraw: true },
  { slug: 'TRON', label: 'TRON (TRX)', deposit: true, withdraw: true },
  { slug: 'LTC', label: 'Litecoin', deposit: true, withdraw: true },
  { slug: 'DOGE', label: 'Dogecoin', deposit: true, withdraw: true },
  { slug: 'XRP', label: 'Ripple (XRP)', deposit: true, withdraw: false },
];

const list = (value) => String(value ?? '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean)
  .map((item) => (item.toLowerCase() === 'checkout' ? 'checkout' : item.toUpperCase()));

export const hasMethod = (value, slug) => list(value).includes(slug);

/** Sıra katalogdan gelir: işaretleme sırası kayda girmez. */
export const toggleMethod = (value, slug, checked) => {
  const current = new Set(list(value));
  if (checked) current.add(slug);
  else current.delete(slug);
  return CRYPTOBOX_METHODS
    .map((item) => item.slug)
    .filter((item) => current.has(item))
    .join(',');
};

const sameValue = (a, b) => {
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && a != null && b != null
      && Number.isFinite(na) && Number.isFinite(nb)) {
    return na === nb;
  }
  return String(a ?? '') === String(b ?? '');
};

// Sırlar yalnızca **yazıldıysa** gönderilir: sunucu onları geri vermez.
const SECRET_FIELDS = ['api_key', 'hmac_secret'];

export const changedFields = (current, initial) => {
  const out = {};
  Object.keys(CRYPTOBOX_DEFAULTS).forEach((key) => {
    if (SECRET_FIELDS.includes(key)) {
      const value = String(current[key] ?? '').trim();
      if (value) out[key] = value;
      return;
    }
    if (!sameValue(current[key], initial[key])) out[key] = current[key];
  });
  return out;
};

/** Kur modunun etiketi; panel ile backend aynı kümeyi kullanır. */
export const RATE_MODES = [
  { key: 'auto', label: 'Otomatik (15 dakikada bir)' },
  { key: 'manual', label: 'Elle gireceğim' },
];

/** Kurun güncelliği: eski bir kur çekimde oyuncuya yanlış miktar gönderir. */
export const rateAgeHours = (updatedAt, now = Date.now()) => {
  if (!updatedAt) return null;
  const raw = String(updatedAt);
  const parsed = Date.parse(raw.endsWith('Z') || raw.includes('+') ? raw : `${raw}Z`);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, (now - parsed) / 36e5);
};
